﻿import * as React from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@material-ui/core";
import { IReturnModel } from "../";
import { PreferenceItem } from ".";

export interface ICommPreferencesProps {
    callBackOnComplete: () => void;
}

export interface ICommPreferencesState {
    canSave: boolean;
    errorMessage: string;
    isSaveDialogOpen: boolean;
    preferences: ICommPreference[];
}

export interface ICommPreference {
    description: string;
    email: boolean;
    id: number;
    name: string;
    notification: boolean;
}

export class CommPreferences extends React.Component<
    ICommPreferencesProps,
    ICommPreferencesState
> {
    constructor(props: ICommPreferencesProps) {
        super(props);

        this.state = {
            canSave: false,
            errorMessage: null,
            isSaveDialogOpen: false,
            preferences: [],
        };
    }

    componentDidMount(): void {
        void this._getPreferences();
    }

    private readonly _getPreferences = async (): Promise<void> => {
        const stringResult: string = await $.ajax(`/Profile/CommPreferences`, {
            method: `GET`,
        });

        const result: IReturnModel<{ preferences: ICommPreference[] }> =
            JSON.parse(stringResult);

        if (result.isSuccess) {
            this.setState({
                canSave: false,
                preferences: result.value.preferences,
            });
        } else {
            this.setState({
                errorMessage: result.errorMessage,
            });
        }
    };

    private readonly _updatePreference = (
        id: number,
        email: boolean,
        notification: boolean,
    ): void => {
        const index: number = this.state.preferences.findIndex(
            (p) => p.id === id,
        );

        this.setState((prevState) => {
            const preferences: ICommPreference[] = prevState.preferences;

            preferences[index].email = email;
            preferences[index].notification = notification;

            return {
                canSave: true,
                preferences,
            };
        });
    };

    private readonly _postPreferences = async (): Promise<void> => {
        const stringResult: string = await $.ajax(`/Profile/CommPreferences`, {
            data: {
                model: {
                    preferences: this.state.preferences,
                },
            },
            method: `POST`,
        });

        const result: IReturnModel<null> = JSON.parse(stringResult);

        if (result.isSuccess) {
            this.setState({
                canSave: false,
            });
        } else {
            this.setState({
                errorMessage: result.errorMessage,
            });
        }
    };

    private readonly _handleClose = (): void => {
        if (this.state.canSave) {
            this.setState({
                isSaveDialogOpen: true,
            });
        } else {
            this.props.callBackOnComplete();
        }
    };

    private readonly _cancelClose = (): void => {
        this.setState({
            isSaveDialogOpen: false,
        });
    };

    private readonly _saveAndClose = async (): Promise<void> => {
        await this._postPreferences();

        if (this.state.errorMessage) {
            this.setState({
                isSaveDialogOpen: false,
            });
        } else {
            this.props.callBackOnComplete();
        }
    };

    render(): React.ReactElement {
        return (
            <div className={`commpreferences`}>
                <div className={`head-closecontainer`}>
                    <div
                        className={`closecontainer-closebutton`}
                        onClick={this._handleClose}
                    >
                        <img src={`/Images/CloseIcon.svg`} />
                    </div>
                </div>
                <h3 className={`commpreferences-title`}>
                    Communication Preferences
                </h3>
                <div>
                    Configure how you would like to handle communications from
                    the different SmartTools modules. For each module you must
                    select at least one communication method.
                </div>
                <hr />
                <table className={`commpreferences-table`}>
                    <thead>
                        <tr>
                            <th />
                            <th>Email</th>
                            <th>Notification</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.preferences.map(
                            (value: ICommPreference) => (
                                <PreferenceItem
                                    key={value.id}
                                    updateParent={this._updatePreference}
                                    {...value}
                                />
                            ),
                        )}
                    </tbody>
                </table>
                {this.state.errorMessage && (
                    <div className={`warningtext`}>
                        {this.state.errorMessage}
                    </div>
                )}
                <div className={`commpreferences-buttons`}>
                    <Button
                        disabled={!this.state.canSave}
                        onClick={this._getPreferences}
                        variant={`text`}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={!this.state.canSave}
                        onClick={this._postPreferences}
                        variant={`contained`}
                    >
                        Save
                    </Button>
                </div>
                <Dialog
                    disableBackdropClick
                    id={`save-dialog`}
                    open={this.state.isSaveDialogOpen}
                >
                    <DialogTitle>Unsaved Changes</DialogTitle>
                    <DialogContent>
                        {" "}
                        Would you like to save your changes before leaving?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this._cancelClose} variant={`text`}>
                            Cancel
                        </Button>
                        <Button
                            onClick={this._saveAndClose}
                            variant={`contained`}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}
